import React, { useState, useEffect } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormServices, UpsertFormRequest } from "@/services/bedrock";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle, Copy, ExternalLink, FlaskConical, WandSparkles, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { FormEditView, FormSchemaType } from "./FormEditView";

const DEFAULT_FORM_DRAFT: UpsertFormRequest = {
  displayName: "New Feedback Form",
  active: true,
  allowThemeChange: true,
  allowAiPersonalization: true,
  allowTranslations: false,
  supportedLanguages: ["en"],
  outroCtaLabel: "Share with a friend",
  outroCtaType: "share_player_link",
  formFields: [],
};

function toUpsertFormRequest(data: Form | UpsertFormRequest | FormSchemaType): UpsertFormRequest {
  return {
    ...data,
    contentPageThemeId: data.contentPageThemeId ?? undefined,
    introTitle: data.introTitle ?? undefined,
    introText: data.introText ?? undefined,
    outroTitle: data.outroTitle ?? undefined,
    outroText: data.outroText ?? undefined,
    outroCtaLabel: data.outroCtaLabel ?? undefined,
    outroCtaType: (data.outroCtaType ?? undefined) as UpsertFormRequest["outroCtaType"],
    outroCtaLink: data.outroCtaLink ?? undefined,
    showSocialsOnOutro: data.showSocialsOnOutro ?? true,
    formFields: data.formFields?.map(field => ({
      ...field,
      subtitle: field.subtitle ?? undefined,
      fieldOptions: field.fieldOptions ?? undefined,
    })),
  };
}

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: Form | UpsertFormRequest | null;
  onCreateRequestClick?: (form: Form) => void;
}

export function FormDialog({ open, onOpenChange, form, onCreateRequestClick }: FormDialogProps) {
  const queryClient = useQueryClient();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [createdForm, setCreatedForm] = useState<Form | null>(null);
  const [copied, setCopied] = useState(false);
  const [formDraft, setFormDraft] = useState<UpsertFormRequest>(DEFAULT_FORM_DRAFT);

  useEffect(() => {
    if (open) {
      setCreatedForm(null);
      setCopied(false);
      if (form) {
        setFormDraft(toUpsertFormRequest(form));
      } else {
        setFormDraft(DEFAULT_FORM_DRAFT);
      }
    } else {
      setValidationError(null);
    }
  }, [open, form]);

  const isEdit = form && "id" in form && typeof (form as any).id === "number";

  const createMutation = useMutation({
    mutationFn: (request: UpsertFormRequest) => FormServices.createForm(request),
    onSuccess: (data: Form) => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      setCreatedForm(data);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (request: UpsertFormRequest) => {
      if (!isEdit || !form || !("id" in form)) throw new Error("No form ID found to update");
      return FormServices.updateForm(form.id, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      onOpenChange(false);
    },
  });

  const handleCopyTestUrl = () => {
    const testUrl = createdForm?.testRequest?.url;
    if (testUrl) {
      navigator.clipboard.writeText(testUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (createdForm) {
    const testUrl = createdForm.testRequest?.url;

    return (
      <Dialog open={open} onOpenChange={onOpenChange} size="lg">
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2 text-xl font-bold font-sans select-none">
            <CheckCircle className="size-6 text-emerald-500" />
            <span>Form Created Successfully!</span>
          </Dialog.Title>
          <Dialog.Description className="select-none">
            Your new feedback form is live and ready for testing.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="space-y-6 py-4 px-6">
          {/* Test Link Section */}
          <div className="bg-slate-50 border border-slate-200/50 p-5 rounded-2xl space-y-4">
            <div className="flex items-center gap-2 text-amber-800 font-bold text-base select-none">
              <FlaskConical className="size-5 text-amber-600" />
              <span>Preview & Test Your Form</span>
            </div>
            <p className="text-sm text-muted-foreground leading-normal select-none">
              We've generated a temporary test link for you to preview and try out your new form. Submissions using this
              link are marked as tests and won't affect your production analytics.
            </p>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <div className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2.5 font-mono text-xs text-slate-700 truncate select-all h-10 flex items-center">
                {testUrl || "Test URL not available"}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "shrink-0 h-10 px-4 rounded-xl flex items-center gap-1.5 font-semibold font-sans text-xs select-none transition-all duration-200",
                    copied ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700" : ""
                  )}
                  onClick={handleCopyTestUrl}
                >
                  <Copy className="size-4" />
                  <span>{copied ? "Copied!" : "Copy"}</span>
                </Button>
                {testUrl && (
                  <a
                    href={testUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 h-10 px-4 text-xs font-semibold rounded-xl bg-primary text-white hover:bg-primary/90 shadow-sm whitespace-nowrap transition-colors select-none"
                  >
                    <span>Open Test Form</span>
                    <ExternalLink className="size-4" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Request Section */}
          <div className="border border-slate-200 p-5 rounded-2xl space-y-4 text-center">
            <div className="flex items-center justify-center gap-2 text-slate-800 font-bold text-base select-none">
              <Users className="size-5 text-slate-500" />
              <span>Generate Links for Players & Clients</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto select-none">
              Create unique, trackable links for individual Players and Clients. Attaching a Player dynamically brands
              that specific link's experience with their profile details and routes all responses and leads directly to
              them.
            </p>
            <div className="pt-2 select-none">
              <Button
                onClick={() => {
                  if (onCreateRequestClick) {
                    onCreateRequestClick(createdForm);
                  }
                }}
                className="w-full sm:w-auto h-11 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 mx-auto"
              >
                <WandSparkles className="size-4" />
                <span>Generate Personalized Link</span>
              </Button>
            </div>
          </div>
        </Dialog.Content>

        <Dialog.Footer>
          <div className="flex justify-end w-full select-none">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      className="h-screen max-h-screen overflow-hidden flex flex-col rounded-none"
    >
      <Dialog.Content className="p-0 flex-1 min-h-0 overflow-hidden">
        <FormEditView
          id="reputation-form-edit"
          initialData={formDraft}
          onValidationError={setValidationError}
          onSubmit={data => {
            const request = toUpsertFormRequest(data);
            if (isEdit) {
              updateMutation.mutate(request);
            } else {
              createMutation.mutate(request);
            }
          }}
        />
      </Dialog.Content>

      <Dialog.Footer className="shrink-0">
        <div className="flex justify-between w-full items-center gap-2">
          <div className="flex items-center gap-1.5 text-sm text-destructive font-medium min-w-0 flex-1">
            {validationError && (
              <>
                <AlertCircle className="size-4 shrink-0" />
                <span className="truncate">{validationError}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="reputation-form-edit"
              loading={isEdit ? updateMutation.isPending : createMutation.isPending}
            >
              {isEdit ? "Save Changes" : "Create Form"}
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}
